import { useEffect, useRef, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, RowSelectedEvent } from "ag-grid-community";
import {
  filterSelectionsType,
  TimeFilterOption,
} from "../interfaces/queryBuilderInterfaces";

const useRowSelection = (filterSelections: filterSelectionsType) => {
  const advancedFilterGridRef = useRef<AgGridReact<TimeFilterOption>>(null);
  const advancedFilterGridRefInfinite =
    useRef<AgGridReact<TimeFilterOption>>(null);
  const querySelected: TimeFilterOption[] = filterSelections.selectedFilters.map(
    (item: {
      name: string;
      caption?: string;
      altCaption?: string;
      uniqueName?: string;
      properties?: any;
      isNamedSet?: boolean;
      selType?: string;
    }) =>
      ({
        name: item.name,
        caption: item.caption ?? item.altCaption ?? item.name,
        altCaption: item.altCaption ?? item.caption ?? item.name,
        uniqueName: item.uniqueName ?? item.name,
        properties: item.properties ?? {},
        isNamedSet: item.isNamedSet,
        selType: item.selType,
      }) as TimeFilterOption,
  );
  const [currentSelections, setCurrentSelections] =
    useState<TimeFilterOption[]>(querySelected);
  const [namedSetSelections, setNamedSetSelections] =
    useState<TimeFilterOption[]>([]);
  const [dataLength, setDataLength] = useState<any>(0);
  const [isExclude, setExclude] = useState(filterSelections.exclude);
  const advancedFilterGridColumn: ColDef<TimeFilterOption>[] = [
    {
      headerName: "Caption",
      field: "altCaption",
      flex: 1,
    },
    {
      headerName: "Name",
      field: "name",
      hide: true,
    },
    {
      headerName: "AltCaption",
      field: "altCaption",
      hide: true,
    },
    {
      headerName: "Unique Name",
      field: "uniqueName",
      hide: true,
    },
  ];

  const handleCheckAll = () => {
    if (advancedFilterGridRef.current) {
      advancedFilterGridRef.current.api.selectAll();
      const selectedNodes =
        advancedFilterGridRef.current.api.getSelectedNodes();
      const selectedRows = selectedNodes
        .map((option) => option.data)
        .filter(Boolean) as TimeFilterOption[];

      setCurrentSelections((prev) => {
        const merged = [...prev];
        selectedRows.forEach((row) => {
          const exists = merged.some((selection) => selection.name === row.name);
          if (!exists) {
            merged.push(row);
          }
        });
        return merged;
      });
    }
  };

  const handleNamedSetCheckAll = () => {
    if (advancedFilterGridRef.current) {
      advancedFilterGridRef.current.api.selectAll();
      const selectedNodes =
        advancedFilterGridRef.current.api.getSelectedNodes();
      const selectedRows = selectedNodes
        .map((option) => option.data)
        .filter(Boolean) as TimeFilterOption[];

      setNamedSetSelections((prev) => {
        const merged = [...prev];
        selectedRows.forEach((row) => {
          const exists = merged.some((selection) => selection.name === row.name);
          if (!exists) {
            merged.push(row);
          }
        });
        return merged;
      });
    }
  };

  const handleUncheckAll = () => {
    if (advancedFilterGridRef.current) {
      advancedFilterGridRef.current.api.deselectAll();
      const filterValues = getFilerValues();
      const updatedSelections = currentSelections.filter(
        (selection) =>
          !filterValues.some((a) => a && a.name === selection.name),
      );
      setCurrentSelections(updatedSelections);
    }
  };

  const handleNamedSetUncheckAll = () => {
    if (advancedFilterGridRef.current) {
      const filterValues = getFilerValues();
      setNamedSetSelections((prev) =>
        prev.filter(
          (selection) =>
            !filterValues.some((a) => a && a.name === selection.name),
        ),
      );
    }
  };

  const getFilerValues = () => {
    const allValues = [];
    if (advancedFilterGridRef.current) {
      const rowCount = advancedFilterGridRef.current.api.getDisplayedRowCount();

      for (let i = 0; i < rowCount; i++) {
        const rowNode =
          advancedFilterGridRef.current.api.getDisplayedRowAtIndex(i);
        if (rowNode) {
          allValues.push(rowNode.data);
        }
      }
    }
    return allValues;
  };

  const clearSelections = (isNamedSet: boolean) => {
    const updatedSelections = currentSelections.filter((selection) => {
      const selectionIsNamedSet = selection.isNamedSet === true;
      return selectionIsNamedSet !== isNamedSet;
    });
    setCurrentSelections(updatedSelections);
  };

  const clearNamedSetSelections = () => {
    setNamedSetSelections([]);
  };

  const isTimeFilterOption = (data: any): data is TimeFilterOption => {
    return (
      typeof data === "object" &&
      data !== null &&
      typeof data.altCaption === "string" &&
      typeof data.uniqueName === "string" &&
      typeof data.name === "string" &&
      typeof data.properties === "object" &&
      data.properties !== null && // Ensure properties is not null
      typeof data.caption === "string"
    );
  };

  const isRowPresentInAnyGrid = (rowName: string) => {
    let isPresent = false;

    if (advancedFilterGridRef.current?.api) {
      advancedFilterGridRef.current.api.forEachNode((node) => {
        if (node.data?.name === rowName) {
          isPresent = true;
        }
      });
    }

    if (!isPresent && advancedFilterGridRefInfinite.current?.api) {
      advancedFilterGridRefInfinite.current.api.forEachNode((node) => {
        if (node.data?.name === rowName) {
          isPresent = true;
        }
      });
    }

    return isPresent;
  };

  const onRowSelected = (event: RowSelectedEvent) => {
    const rowData = event.node.data as TimeFilterOption;
    if (!rowData?.name) {
      return;
    }

    // Keep selections when rows disappear from view due to search/show-selected filters.
    if (!event.node.isSelected() && !isRowPresentInAnyGrid(rowData.name)) {
      return;
    }

    setCurrentSelections((prev) => {
      const next = prev.filter((selection) => selection.name !== rowData.name);
      if (event.node.isSelected()) {
        next.push(rowData);
      }
      return next;
    });
  };

  const onNamedSetRowSelected = (event: RowSelectedEvent) => {
    const rowData = event.node.data as TimeFilterOption;
    if (!rowData?.name) {
      return;
    }

    // Keep named set selections when rows disappear from view due to search/show-selected filters.
    if (!event.node.isSelected() && !isRowPresentInAnyGrid(rowData.name)) {
      return;
    }


    setNamedSetSelections((prev) => {
      const next = prev.filter((selection) => selection.name !== rowData.name);
      if (event.node.isSelected()) {
        next.push(rowData);
      }
      return next;
    });
  };
  const onCellClicked = (event: {
    node: { setSelected: (arg0: boolean) => void; isSelected: () => any };
  }) => {
    event.node.setSelected(!event.node.isSelected());
  };
  const selectRowsByArray = (rowsToSelect: TimeFilterOption[]) => {
    if (advancedFilterGridRef.current && advancedFilterGridRef.current.api) {
      advancedFilterGridRef.current.api.forEachNode((node) => {
        const isSelected = rowsToSelect.some(
          (row) => row.name === node.data?.name,
        );
        node.setSelected(isSelected);
      });
    }
    if (advancedFilterGridRefInfinite.current) {
      advancedFilterGridRefInfinite.current.api.forEachNode((node) => {
        const isSelected = rowsToSelect.some(
          (row) => row.name === node.data?.name,
        );
        node.setSelected(isSelected);
      });
    }
  };

  const selectRowsByCurrentSelections = () => {
    if (advancedFilterGridRef.current) {
      advancedFilterGridRef.current.api.forEachNode((node) => {
        const isSelected = currentSelections.some(
          (row) => row.name === node.data?.name,
        );
        node.setSelected(isSelected);
      });
    }
    if (advancedFilterGridRefInfinite.current) {
      advancedFilterGridRefInfinite.current.api.forEachNode((node) => {
        const isSelected = currentSelections.some(
          (row) => row.name === node.data?.name,
        );
        node.setSelected(isSelected);
      });
    }
  };

  const checkIsAllSelected = () => {
    if (currentSelections && currentSelections.length > 0) {
      if (dataLength > 0 && dataLength === currentSelections.length) {
        return true;
      }
      return false;
    }
    return false;
  };
  const setOptionsLength = (length: number) => {
    setDataLength(length);
  };

  return {
    advancedFilterGridRef,
    advancedFilterGridRefInfinite,
    advancedFilterGridColumn,
    handleCheckAll,
    handleNamedSetCheckAll,
    handleUncheckAll,
    handleNamedSetUncheckAll,
    onRowSelected,
    onNamedSetRowSelected,
    onCellClicked,
    selectRowsByArray,
    currentSelections,
    namedSetSelections,
    setOptionsLength,
    selectRowsByCurrentSelections,
    clearSelections,
    clearNamedSetSelections,
    isExclude,
    setExclude,
    checkIsAllSelected,
  };
};

export default useRowSelection;
